import {ComponentProps, FunctionComponent, ReactElement} from 'react';
import clsx from 'clsx';
import {twMerge} from 'tailwind-merge';
import Spinner from '@/components/spinner';
import {TrackedButton} from '@/components/tracked-atoms/tracked-button';
import {
  BUTTON_ICON_POSITION,
  BUTTON_THEME,
  BUTTON_VARIANT,
  ButtonIconPosition,
  ButtonTheme,
  ButtonVariant,
} from '@/constants/button';

export type ButtonProps = ComponentProps<typeof TrackedButton> & {
  icon?: ReactElement;
  iconPosition?: ButtonIconPosition;
  label?: string | ReactElement;
  theme?: ButtonTheme;
  variant?: ButtonVariant;
  allowClickOnDisabled?: boolean;
  isLoading?: boolean;
};

const CLASSES_BASED_ON_THEME = {
  [BUTTON_THEME.PRIMARY]: 'bg-violet text-white hover:bg-purple-925 [&_svg]:fill-white',
  [BUTTON_THEME.SECONDARY]:
    'bg-white text-violet border-violet border-2 hover:bg-violet hover:text-white [&_svg]:fill-violet [&_svg]:hover:fill-white',
  [BUTTON_THEME.TERTIARY]: 'bg-white text-violet hover:bg-purple-100 [&_svg]:fill-violet',
  [BUTTON_THEME.STYLELESS]: 'text-violet',
  [BUTTON_THEME.DANGER]: 'text-red hover:bg-red-100 hover:text-red/80 [&_svg]:fill-red',
};

const DISABLED_CLASSES_BASED_ON_THEME = {
  [BUTTON_THEME.PRIMARY]: 'bg-grey-400 hover:bg-grey-400 text-grey-650 [&_svg]:fill-grey-650',
  [BUTTON_THEME.SECONDARY]:
    'bg-grey-400 hover:bg-grey-400 text-grey-650 hover:text-grey-650 [&_svg]:fill-grey-650 [&_svg]:hover:fill-grey-650 border-0',
  [BUTTON_THEME.TERTIARY]: 'text-grey-400 [&_svg]:fill-grey-400',
  [BUTTON_THEME.STYLELESS]: 'text-grey-400',
  [BUTTON_THEME.DANGER]: 'text-grey-400 hover:text-grey-400 hover:bg-transparent [&_svg]:fill-grey-650',
};

const CLASSES_BASED_ON_VARIANT = {
  [BUTTON_VARIANT.DEFAULT]: '',
  [BUTTON_VARIANT.X_SMALL]: 'min-w-0 min-h-0 px-4 py-1 text-sm',
  [BUTTON_VARIANT.SMALL]: '',
  [BUTTON_VARIANT.FULL_WIDTH]: 'w-full justify-center',
  [BUTTON_VARIANT.FORM_FIELD]: 'w-full justify-between p-4 !rounded-2 min-h-[58px]',
};

const Button: FunctionComponent<ButtonProps> = ({
  className,
  disabled,
  allowClickOnDisabled,
  icon,
  iconPosition = BUTTON_ICON_POSITION.LEFT,
  label,
  theme = BUTTON_THEME.PRIMARY,
  variant = BUTTON_VARIANT.DEFAULT,
  isLoading = false,
  ...props
}) => {
  const {type} = props;

  const isIconOnly = !label && icon;
  const buttonType = type ?? 'button';
  const isDisabled = isLoading || (disabled && !allowClickOnDisabled);
  const trackingUseDisabledSuffix = isLoading || disabled;

  const themeClasses = CLASSES_BASED_ON_THEME[theme];
  const variantClasses = CLASSES_BASED_ON_VARIANT[variant];
  const disabledClasses = disabled ? DISABLED_CLASSES_BASED_ON_THEME[theme] : '';

  const mergedClasses = clsx(
    'flex min-h-11 min-w-24 items-center justify-center gap-2 rounded-5 px-4 py-3 text-base font-medium leading-0 transition-colors duration-200 ease-in-out [&_svg]:transition-colors [&_svg]:duration-200 [&_svg]:ease-in-out',
    themeClasses,
    variantClasses,
    disabledClasses,
    className,
    label && icon && iconPosition === BUTTON_ICON_POSITION.LEFT && 'flex-row-reverse',
    icon && iconPosition === BUTTON_ICON_POSITION.RIGHT && 'flex-row',
    isIconOnly && 'size-14 min-h-0 min-w-0 shrink-0 justify-center p-2',
    isIconOnly && variant === BUTTON_VARIANT.SMALL && 'size-6',
    isDisabled && 'cursor-not-allowed',
  );

  return (
    <TrackedButton
      disabled={isDisabled}
      className={twMerge(mergedClasses)}
      type={buttonType}
      trackingUseDisabledSuffix={trackingUseDisabledSuffix}
      {...props}
    >
      {label && <>{label}</>}
      {isLoading ? <Spinner className="size-4" /> : icon}
    </TrackedButton>
  );
};

export default Button;
